import React, { useContext, useState, useRef, useEffect } from "react";
import GlobalContext from "./../context/Global.ts";
import StoriesContext from "./../context/Stories.ts";
import ProgressContext from "./../context/Progress.ts";
import Story from "./Story.tsx";
import ProgressArray from "./ProgressArray.tsx";
import {
  GlobalCtx,
  StoriesContext as StoriesContextInterface,
} from "./../interfaces";
import useIsMounted from "./../util/use-is-mounted.tsx";
import { usePreLoader } from "../util/usePreLoader.ts";
import mixpanel from "mixpanel-browser";

export default function () {
  const [currentId, setCurrentId] = useState<number>(0);
  const [pause, setPause] = useState<boolean>(true);
  const [bufferAction, setBufferAction] = useState<boolean>(true);
  const [videoDuration, setVideoDuration] = useState<number>(0);
  const [touchStartY, setTouchStartY] = useState<any>(null);
  const isMounted = useIsMounted();

  let mousedownId = useRef<any>();

  const {
    width,
    height,
    loop,
    currentIndex,
    isPaused,
    keyboardNavigation,
    preventDefault,
    storyContainerStyles = {},
    onAllStoriesEnd,
    onPrevious,
    onNext,
    preloadCount,
    onSwipeUp
  } = useContext<GlobalCtx>(GlobalContext);
  const { stories } = useContext<StoriesContextInterface>(StoriesContext);
  
  usePreLoader(stories, currentId, preloadCount);

  useEffect(() => {
    if (typeof currentIndex === "number") {
      if (currentIndex >= 0 && currentIndex < stories.length) {
        setCurrentIdWrapper(() => currentIndex);
      } else {
        console.error(
          "Index out of bounds. Current index was set to value more than the length of stories array.",
          currentIndex
        );
      }
    }
  }, [currentIndex]);

  useEffect(() => {
    if (typeof isPaused === "boolean") {
      setPause(isPaused);
    }
  }, [isPaused]);

  useEffect(() => {
    const isClient = typeof window !== "undefined" && window.document;
    if (
      isClient &&
      typeof keyboardNavigation === "boolean" &&
      keyboardNavigation
    ) {
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [keyboardNavigation]);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "ArrowLeft") {
      previous();
    } else if (e.key === "ArrowRight") {
      next({ isSkippedByUser: true });
    }
  };

  const toggleState = (action: string, bufferAction?: boolean) => {
    setPause(action === "pause");
    setBufferAction(!!bufferAction);
  };

  const setCurrentIdWrapper = (callback) => {
    setCurrentId(callback);
    toggleState("pause", true);
  };

  const previous = () => {
    mixpanel.track("landing_explore-story-prev-skip");
    if (onPrevious != undefined) {
      onPrevious();
    }
    setCurrentIdWrapper((prev) => (prev > 0 ? prev - 1 : prev));
  };

  const next = (options?: { isSkippedByUser?: boolean }) => {
    if (options?.isSkippedByUser) {
      mixpanel.track("landing_explore-story-next-skip");
    } else {
      mixpanel.track("landing_explore-story-next-auto");
    }
    if (onNext != undefined && options?.isSkippedByUser) {
      onNext();
    }
    if (isMounted()) {
      if (loop) {
        updateNextStoryIdForLoop();
      } else {
        updateNextStoryId();
      }
    }
  };

  const updateNextStoryIdForLoop = () => {
    setCurrentIdWrapper((prev) => {
      if (prev >= stories.length - 1) {
        onAllStoriesEnd && onAllStoriesEnd(currentId, stories);
      }
      return (prev + 1) % stories.length;
    });
  };

  const updateNextStoryId = () => {
    setCurrentIdWrapper((prev) => {
      if (prev < stories.length - 1) return prev + 1;
      onAllStoriesEnd && onAllStoriesEnd(currentId, stories);
      return prev;
    });
  };

  const debouncePause = () => {
    mousedownId.current = setTimeout(() => {
      toggleState("pause");
    }, 300);
  };

  const handleTouchStart = (e) => {
    e.preventDefault(); // now allowed because of passive: false
    setTouchStartY(e.touches[0].clientY);
    debouncePause();
  };

  const handleTouchEnd = (direction) => (e) => {
    const touchEndY = e.changedTouches[0].clientY;

    if (touchStartY && touchStartY - touchEndY > 50) {
      const currentStory = stories[currentId];
      if (onSwipeUp && currentStory?.enableSwipeUp) {
        mixpanel.track("landing_explore-join-waitlist-swipeup");
        onSwipeUp();
      }
    }

    mouseUp(direction)(e);
  };

  const mouseUp = (type: string) => (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    mousedownId.current && clearTimeout(mousedownId.current);
    if (pause) {
      toggleState("play");
    } else {
      if ((currentId < stories.length - 1) && type === "next") {
        next({ isSkippedByUser: true });
      } else if (type !== "next" && currentId !== 0) {
        previous();
      }
    }
  };

  const getVideoDuration = (duration: number) => {
    setVideoDuration(duration * 1000);
  };

  // Add native event listeners with passive: false
  useEffect(() => {
    const left = document.getElementById("left-touch-zone");
    const right = document.getElementById("right-touch-zone");

    if (left) {
      left.addEventListener("touchstart", handleTouchStart, { passive: false });
    }
    if (right) {
      right.addEventListener("touchstart", handleTouchStart, { passive: false });
    }

    return () => {
      if (left) {
        left.removeEventListener("touchstart", handleTouchStart);
      }
      if (right) {
        right.removeEventListener("touchstart", handleTouchStart);
      }
    };
  }, []);

  return (
    <div
      style={{
        ...styles.container,
        ...storyContainerStyles,
        ...{ width, height },
      }}
    >
      <ProgressContext.Provider
        value={{
          bufferAction: bufferAction,
          videoDuration: videoDuration,
          currentId,
          pause,
          next,
        }}
      >
        <ProgressArray />
      </ProgressContext.Provider>
      <Story
        action={toggleState}
        bufferAction={bufferAction}
        playState={pause}
        story={stories[currentId]}
        getVideoDuration={getVideoDuration}
      />
      {!preventDefault && (
        <div style={styles.overlay}>
          <div
            id="left-touch-zone"
            style={{ width: "50%", height: "100%", float: "left", zIndex: 999 }}
            onTouchEnd={handleTouchEnd("previous")}
            onMouseDown={debouncePause}
            onMouseUp={mouseUp("previous")}
            onClick={() => previous()}
          />
          <div
            id="right-touch-zone"
            style={{ width: "50%", height: "100%", float: "right", zIndex: 999 }}
            onTouchEnd={handleTouchEnd("next")}
            onMouseDown={debouncePause}
            onMouseUp={mouseUp("next")}
            onClick={() => next({ isSkippedByUser: true })}
          />
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as const,
    background: "#111",
    position: "relative" as const,
    WebkitUserSelect: 'none' as const,
  },
  overlay: {
    position: "absolute" as const,
    height: "inherit",
    width: "inherit",
    display: "flex",
  },
};
